import React, { Component, createRef } from 'react';
import Select, { type MultiValue, type InputActionMeta } from 'react-select';
import { filterService } from '@services/filterService';
import type { FilterOption } from '../../types/dashboard';
import type { ApiConfig } from '@components/ConversationItem/types';

import './InputFilter.css';

// ==================== Types ====================

interface SelectedFilter {
  Name: string;
  Value: string;
  Text: string;
  IsCustomField: boolean;
  FieldTypeId: number | null;
  CustomFields: [] | null;
  ComparisonOperator: string;
  isChipFilter: boolean;
}

interface InputFilterProps {
  request: {
    api: ApiConfig;
    functions?: Record<string, any>;
    params?: Record<string, any>;
    config?: Record<string, any>;
    storageData?: any;
    chatData?: any;
    userData?: any;
  };
  isFromProject?: boolean;
  configValueId?: number;
  getConversationByFilters: (filters: SelectedFilter[]) => void;
}

interface InputFilterState {
  filters: FilterOption[];
  showSelect: boolean;
  hiddenFiltersAmount: number;
  showingAllTags: boolean;
  filtersSelectedIds: string[];
  filtersSelected: FilterOption[];
  selectedFilters: SelectedFilter[];
  noResultsText: string;
}

// ==================== Helper ====================

function uniqueObjects<T>(arr: T[]): T[]
{
  const result: T[] = [];
  for (let i = 0; i < arr.length; i++)
  {
    const item = arr[i];
    if (item === undefined) continue;
    let isDuplicate = false;
    for (let j = 0; j < result.length; j++)
    {
      if (JSON.stringify(item) === JSON.stringify(result[j]))
      {
        isDuplicate = true;
        break;
      }
    }
    if (!isDuplicate)
    {
      result.push(item);
    }
  }
  return result;
}

// ==================== Component ====================

class InputFilter extends Component<InputFilterProps, InputFilterState>
{
  private timerChangeSearchFilter: ReturnType<typeof setTimeout> | null = null;
  private filterButtonRef = createRef<HTMLDivElement>();
  private tagsParentContainerRef = createRef<HTMLDivElement>();
  private tagsContainerRef = createRef<HTMLDivElement>();
  private showMoreTagsButtonRef = createRef<HTMLDivElement>();

  constructor(props: InputFilterProps)
  {
    super(props);

    this.state = {
      filters: [],
      showSelect: false,
      hiddenFiltersAmount: 0,
      showingAllTags: false,
      filtersSelectedIds: [],
      filtersSelected: [],
      selectedFilters: [],
      noResultsText: 'No results found',
    };
  }

  componentDidMount()
  {
    const element = this.filterButtonRef.current;
    if (element)
    {
      element.setAttribute('ht-name', 'Filters the dashboard by details, dates, etc.');
    }
  }

  componentWillUnmount()
  {
    if (this.timerChangeSearchFilter)
    {
      clearTimeout(this.timerChangeSearchFilter);
    }
  }

  private onInputChangeFilter = (newValue: string, _actionMeta: InputActionMeta) =>
  {
    if (newValue !== undefined && newValue.length > 0)
    {
      if (this.timerChangeSearchFilter)
      {
        clearTimeout(this.timerChangeSearchFilter);
      }
      this.timerChangeSearchFilter = setTimeout(() =>
      {
        this.getFiltersOptions(newValue);
      }, 500);
    }
  };

  private getConversationByFilters = (e: React.MouseEvent<HTMLButtonElement>) =>
  {
    e.preventDefault();
    if (this.props.getConversationByFilters)
    {
      this.props.getConversationByFilters(this.state.selectedFilters);
      this.setState({ showSelect: false });
    }
  };

  private showSelectFilters = () =>
  {
    const showSelectValue = !this.state.showSelect;
    this.setState({ showSelect: showSelectValue });

    if (showSelectValue)
    {
      this.getFiltersOptions();
    }
  };

  private getFiltersOptions = (filterParam?: string) =>
  {
    const params = {
      Filter: filterParam,
      IsFromProject: this.props.isFromProject ?? false,
      ConfigValueId: this.props.configValueId ?? 1,
    };

    this.setState({ noResultsText: 'No results found' }, () =>
    {
      filterService
        .getFilterOptions(null, params)
        .then(result =>
        {
          this.setState({ filters: result });
        })
        .catch(() =>
        {
          this.setState({ noResultsText: 'The results could not be loaded.' });
        });
    });
  };

  private onChangeFilter = (newValue: MultiValue<FilterOption>) =>
  {
    const itemsSelected = [...newValue];
    const copyList = [...this.state.filtersSelected];
    const newList = uniqueObjects([...copyList, ...itemsSelected]);

    const selectedValuesCopy = newList.map(item => item.Name);
    const selectedFilters: SelectedFilter[] = newList.map(item => ({
      Name: item.FieldName,
      Value: item.Id,
      Text: item.Name,
      IsCustomField: item.IsCustomField,
      FieldTypeId: item.FieldTypeId,
      CustomFields: item.IsCustomField ? item.CustomFields : null,
      ComparisonOperator: item.ComparisonOperator,
      isChipFilter: item.Id === undefined || item.Id === null,
    }));

    this.setState(
      {
        filtersSelectedIds: selectedValuesCopy,
        filtersSelected: newList,
        selectedFilters: selectedFilters,
      },
      () =>
      {
        setTimeout(() => this.enableShowMoreTags());
      },
    );
  };

  private removeFilterSelected = (index: number) =>
  {
    const copyList = [...this.state.filtersSelected];
    copyList.splice(index, 1);

    const selectedValuesCopy = copyList.map(item => item.Name);
    const selectedFilters: SelectedFilter[] = copyList.map(item => ({
      Name: item.FieldName,
      Value: item.Id,
      Text: item.Name,
      IsCustomField: item.IsCustomField,
      FieldTypeId: item.FieldTypeId,
      CustomFields: item.IsCustomField ? item.CustomFields : null,
      ComparisonOperator: item.ComparisonOperator,
      isChipFilter: item.Id === undefined || item.Id === null,
    }));

    this.setState(
      {
        filtersSelectedIds: selectedValuesCopy,
        filtersSelected: copyList,
        selectedFilters: selectedFilters,
      },
      () =>
      {
        setTimeout(() => this.enableShowMoreTags());
      },
    );
  };

  private enableShowMoreTags = () =>
  {
    const tagsContainer = this.tagsContainerRef.current;
    const moreTagsButton = this.showMoreTagsButtonRef.current;
    const parent = this.tagsParentContainerRef.current;

    if (!tagsContainer || !moreTagsButton || !parent) return;

    const tags = tagsContainer.querySelectorAll('.filter');
    let counter = 0;

    if (tags.length > 0)
    {
      const firstTagTop = (tags[0] as HTMLElement).offsetTop;
      for (let x = 1; x < tags.length; x++)
      {
        if ((tags[x] as HTMLElement).offsetTop > firstTagTop)
        {
          counter++;
        }
      }
    }

    this.setState({ hiddenFiltersAmount: counter });

    if (!parent.classList.contains('show-more-filter-tags'))
    {
      if (counter > 0)
      {
        moreTagsButton.innerText = 'Show ' + counter + ' More';
        moreTagsButton.style.display = 'inline-flex';
      }
      else
      {
        moreTagsButton.style.display = 'none';
        this.setState({ showingAllTags: false });
        parent.classList.remove('show-more-filter-tags');
      }
    }
    else
    {
      if (counter > 0)
      {
        moreTagsButton.innerText = 'Show Less';
        moreTagsButton.style.display = 'inline-flex';
      }
      else
      {
        moreTagsButton.style.display = 'none';
        this.setState({ showingAllTags: false });
        parent.classList.remove('show-more-filter-tags');
      }
    }
  };

  private showMoreItems = () =>
  {
    const parent = this.tagsParentContainerRef.current;
    const button = this.showMoreTagsButtonRef.current;
    if (!parent || !button) return;

    const counter = this.state.hiddenFiltersAmount;
    const state = this.state.showingAllTags;

    if (state)
    {
      parent.classList.remove('show-more-filter-tags');
      button.innerText = 'Show ' + counter + ' More';
    }
    else
    {
      parent.classList.add('show-more-filter-tags');
      button.innerText = 'Show Less';
    }

    this.setState({ showingAllTags: !state });
  };

  render()
  {
    const { filters, showSelect, filtersSelectedIds, filtersSelected, noResultsText, showingAllTags } =
      this.state;

    let filterClass = 'corgee_com_filters-expandable-btn enable-help-tooltip';
    let showFiltersClass = 'corgee_com_filters-floated-container';

    if (showSelect)
    {
      filterClass += ' filters-enabled open-filter-module';
      showFiltersClass += ' show-filters';
    }

    return (
      <div className="corgee_com_filters">
        <div className="corgee_com_filters-container">
          <div className="corgee_com_filters-expandable-container">
            <div
              className={filterClass}
              onClick={this.showSelectFilters}
              ref={this.filterButtonRef}
            >
              <i className="fa fa-filter"></i>
            </div>
            <div className="corgee_com_filters-ex-read-only-filter-tags">
              {filtersSelectedIds &&
                filtersSelectedIds.map((item, index) => (
                  <span
                    className="corgee_com_filters-ex-read-only-filter-tags-text"
                    key={'filtern-' + index}
                  >
                    {item}
                  </span>
                ))}
            </div>
          </div>

          <div className={showFiltersClass}>
            <div className="corgee_com_filters-floated-filter-select-container">
              {showSelect && (
                <div className="corgee_com_filters-floated-filter-select">
                  <Select<FilterOption, true>
                    classNamePrefix="react-select"
                    autoFocus={true}
                    value={filtersSelected}
                    options={filters}
                    isMulti={true}
                    isSearchable={true}
                    getOptionLabel={option => option.Name}
                    getOptionValue={option => option.Name}
                    placeholder="Add a Filter"
                    onChange={this.onChangeFilter}
                    closeMenuOnSelect={false}
                    isClearable={false}
                    onInputChange={this.onInputChangeFilter}
                    blurInputOnSelect={false}
                    noOptionsMessage={() => noResultsText}
                    controlShouldRenderValue={false}
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    classNames={{
                      menuPortal: () => 'corgee_com_filters-floated-filter-select-portal',
                    }}
                    styles={{
                      menu: base => ({
                        ...base,
                        width: 350,
                        zIndex: 9999,
                      }),
                      menuPortal: base => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                </div>
              )}

              <div
                className={
                  'corgee_com_filters-floated-filter-tags' + (showingAllTags ? ' expanded' : '')
                }
              >
                <div
                  className="corgee_com_filters-floated-filter-tags-group"
                  ref={this.tagsParentContainerRef}
                >
                  <div ref={this.tagsContainerRef} className="counter-helper">
                    {filtersSelected &&
                      filtersSelected.map((item, index) => (
                        <span
                          key={'filter-' + index}
                          className="corgee_com_filters-floated-filter-tags-filter filter"
                        >
                          <span className="corgee_com_filters-floated-filter-tag">
                            <span className="text">{item.Name}</span>
                            <span
                              className="action remove"
                              onClick={() => this.removeFilterSelected(index)}
                            >
                              <i className="fa fa-times"></i>
                            </span>
                          </span>
                        </span>
                      ))}
                  </div>
                </div>
              </div>

              <div className="corgee_com_filters-floated-filter-action-buttons">
                <div
                  ref={this.showMoreTagsButtonRef}
                  className="corgee_com_filters-floated-show-more"
                  onClick={this.showMoreItems}
                  style={{ display: 'none' }}
                >
                  Show More
                </div>
                <button
                  className="corgee_com_button-filter-action"
                  onClick={this.getConversationByFilters}
                >
                  Filter
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default InputFilter;
